import React from "react";
import { useMemo, useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-table components
import {
  useTable,
  usePagination,
  useGlobalFilter,
  useAsyncDebounce,
  useSortBy,
  useGroupBy,
  useExpanded,
  useResizeColumns
} from "react-table";

// @mui material components
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Icon from "@mui/material/Icon";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftSelect from "components/SoftSelect";
import SoftInput from "components/SoftInput";
import SoftPagination from "components/SoftPagination";
import SoftButton from "components/SoftButton";
import SoftDatePicker from "components/SoftDatePicker";

// Soft UI Dashboard PRO React example components
import DataTableHeadCell from "cxfcomponents/Tables/DataTable/DataTableHeadCell";
import DataTableBodyCell from "cxfcomponents/Tables/DataTable/DataTableBodyCell";
import ViewButtons from "./components/ViewButtons";
import View from "./components/View";
import ProtectedButton from "authentication/ProtectedButton";
import LogsFilter from "layouts/pages/logs/components/LogsFilter";
import CredentialManagementSelectAll from "layouts/pages/credential-management/components/CredentialManagementSelectAll";
import { openNewFlowDialog } from "redux/actions/portal/flowsActionCreator";
import { openRegisterComponentDialog } from "redux/actions/flowDesigner/cxfComponentsActionCreator";
import { useSelector } from "react-redux";
import "./css/tableStyle.css";
import { openNewIntegrationDialog } from "redux/actions/portal/integrationActionCreator";
import CredentialManagementFilter from "layouts/pages/credential-management/components/CredentialManagementFilter";
import { PERMISSIONS } from "constants/portal";
import AddIcon from "@mui/icons-material/Add";
import { ThemedReloadIcon } from "assets/CXFIcons/Static";
import { getEnv } from "helpers/environment";
import { useTheme } from "@mui/material/styles";
import { THEME_COLORS } from "constants/theme";

const dataTablePartialDefaultProps = {
  entriesPerPage: { defaultValue: 25, entries: [5, 10, 15, 20, 25] },
  pagination: { variant: "gradient", color: "info" },
  options: {
    roles: [{ value: "", label: "" }],
    status: [{ value: "", label: "" }],
    companies: [{ value: "", label: "" }],
    customers: [{ value: "", label: "" }]
  }
};

function DataTable({
  options = dataTablePartialDefaultProps.options,
  entriesPerPage = dataTablePartialDefaultProps.entriesPerPage,
  canFilterDates = false,
  canFilterCompany = false,
  canFilterCustomer = false,
  canFilterPublishStatus,
  showTotalEntries = true,
  table,
  pagination = dataTablePartialDefaultProps.pagination,
  isSorted = true,
  noEndBorder = true,
  hover = false,
  setOpenInviteModal,
  noFilter,
  hiddenTopButtons,
  reloadUsersList,
  onFiltersChange = new Function(),
  onRowClick = new Function(),
  handleOpenImportFlowDialog,
  buttonPermissions,
  flowView,
  setFlowView,
  setFilterDate,
  canFilter,
  tableView,
  setTableView,
  splitted = false,
  tileViewComponent,
  selections,
  setSelection,
  reloadTableData,
  withSearchbar = false,
  withStatusFilter = false,
  withTileview = false,
  withConnectorsOptions = false,
  withFlowsOptions = false,
  withRoleFilter = false,
  withIntegrationsOptions = false,
  withLogsFilter = false,
  isShareUnshareModal = false,
  withCredentialOptions = false,
  withUsersOptions = false,
  noTopPart = false,
  withCalendarView = false,
  calendarViewComponent,
  setOpenSchedulerDialog,
  withScheduleOptions,
  stayVisible,
  setOpenAddRoleModal,
  reloadRoleList,
  tableHeight
}) {
  const defaultValue = entriesPerPage.defaultValue ? entriesPerPage.defaultValue : 10;
  const entries = entriesPerPage.entries ? entriesPerPage.entries : [5, 10, 15, 20, 25];
  const [data, setData] = useState(table.rows);
  const [view, setView] = useState(1);
  const columns = useMemo(() => table.columns, [table]);
  const { pathname } = useLocation();
  const route = pathname.split("/").slice(1);
  const [searchName, setSearchName] = useState("");
  const dispatch = useDispatch();
  const { isLoadingLogs } = useSelector((store) => store?.portal?.logs);
  const theme = useTheme();
  const isDarkTheme = theme.palette.mode === "dark";

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: {
        pageIndex: 0,
        hiddenColumns: columns.map((column) => {
          if (column.show === false) return column.accessor || column.id;
        }),
        groupBy: columns.map((column) => {
          if (column.grouped === true) return column.accessor;
        }),
        expanded: {
          "sharedStatus:Shared": true,
          "sharedStatus:Owned": true,
          "sharedStatus:Our Connectors": true,
          "sharedStatus:New Connectors": true,
          "sharedStatus:Popular Connectors": true,
          "ownedPurchased:Owned": true,
          "ownedPurchased:Purchased": true
        },
        sortBy: columns
          .filter((column) => column.defaultSortOrder)
          .map((column) => {
            if (column.disableSortBy === false && column.defaultSortOrder)
              return {
                id: column.accessor,
                desc: column.defaultSortOrder === "desc" ? true : false
              };
          })
      },
      autoResetSortBy: false,
      autoResetPage: false
    },
    useGroupBy,
    useGlobalFilter,
    useSortBy,
    useExpanded,
    useResizeColumns,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    pageOptions = [],
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
    state: { pageIndex, pageSize }
  } = tableInstance;

  // Set the default value for the entries per page when component mounts
  useEffect(() => setPageSize(defaultValue || 10), [defaultValue]);

  // Set the entries per page value based on the select value
  const setEntriesPerPage = ({ value }) => setPageSize(value);

  // Render the paginations
  const renderPagination = pageOptions.map((option) => (
    <SoftPagination
      item
      key={option}
      onClick={() => gotoPage(Number(option))}
      active={pageIndex === option}
    >
      {option + 1}
    </SoftPagination>
  ));

  // Handler for the input to set the pagination index
  const handleInputPagination = ({ target: { value } }) =>
    value > pageOptions.length || value < 0 ? gotoPage(0) : gotoPage(Number(value));

  // Customized page options starting from 1
  const customizedPageOptions = pageOptions.map((option) => option + 1);

  // Setting value for the pagination input
  const handleInputPaginationValue = ({ target: value }) => gotoPage(Number(value.value - 1));

  // Search input state handle
  const onSearchChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 500);

  let firstDayOfMonth, lastDayOfMonth;
  if (canFilterDates) {
    let date_today = new Date();

    firstDayOfMonth = new Date(date_today.getFullYear(), date_today.getMonth(), 1);
    lastDayOfMonth = new Date(date_today.getFullYear(), date_today.getMonth() + 1, 0);
  }

  // Filter value state
  const [filter, setFilter] = useState({
    role: "",
    status: "",
    publishStatus: "",
    company: "",
    customer: "",
    start: firstDayOfMonth || "",
    end: lastDayOfMonth || "",
    library: "connector"
  });

  // Filter select state handle
  const onFilterChange = (name, value) => {
    setFilter({ ...filter, [name]: value });
  };

  const handleOpenInviteModal = () => {
    setOpenInviteModal(true);
  };

  const handleOpenAddRoleModal = useCallback(() => {
    setOpenAddRoleModal(true);
  });

  useEffect(() => {
    if (table?.rows) {
      const filteredData = table.rows.filter((row) => {
        const userInput = searchName.trim().toLowerCase();

        return Object.entries(row).some(([, value]) => {
          if (typeof value === "string" || typeof value === "number") {
            return String(value).toLowerCase().includes(userInput);
          } else if (React.isValidElement(value)) {
            const textContent = value.props?.children || value.props?.value || "";
            return String(textContent).toLowerCase().includes(userInput);
          }
          return false;
        });
      });

      setData(filteredData);
      gotoPage(0);
    }
  }, [searchName, table?.rows]);

  const renderView = () => {
    if (tableView === "tiles" && tileViewComponent) {
      return tileViewComponent(page);
    }

    if (tableView === "calendar" && calendarViewComponent) {
      return calendarViewComponent;
    }

    if (view === 1 || route[1] === "users") {
      return (
        <Table {...getTableProps()} data-testid="list" className="table">
          <SoftBox component="thead">
            {headerGroups.map((headerGroup) => {
              const { key, headerGroupProps } = headerGroup.getHeaderGroupProps();

              return (
                <TableRow key={key} {...headerGroupProps}>
                  {headerGroup.headers.map((column, columnIndex) => {
                    const { key, ...headerProps } = column.getHeaderProps(
                      isSorted && column.getSortByToggleProps()
                    );

                    return (
                      <DataTableHeadCell
                        key={key}
                        {...headerProps}
                        className="th"
                        width={column.width ? column.width : "auto"}
                        align={column.align ? column.align : "left"}
                        sorted={column.disableSortBy ? false : setSortedValue(column)}
                        {...(columnIndex === 0
                          ? { paddingLeft: column.paddingLeft ? column.paddingLeft : "24px" }
                          : {})}
                      >
                        {column.render("Header")}
                        {!column.disableResize && column.canResize && (
                          <div
                            {...column.getResizerProps()}
                            className={`resizer ${column.isResizing ? "isResizing" : ""}`}
                          />
                        )}
                      </DataTableHeadCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </SoftBox>
          <TableBody {...getTableBodyProps()}>
            {page.map((row, rowIndex) => {
              prepareRow(row);
              const hoverEffect = hover && !!row.original;
              const { key, ...rowProps } = row.getRowProps();

              return (
                <TableRow
                  key={key}
                  {...rowProps}
                  onClick={() => onRowClick(row.original)}
                  sx={{
                    cursor: hoverEffect ? "pointer" : "default"
                  }}
                  style={{ height: "75px" }}
                  hover={hoverEffect}
                >
                  {row.cells.map((cell, cellIndex) => {
                    const { key, ...cellProps } = cell.getCellProps();
                    const rowsLength = rows?.length;

                    return (
                      <DataTableBodyCell
                        key={key}
                        noBorder={noEndBorder && rowsLength === rowIndex + 1}
                        align={cell.column.align ? cell.column.align : "left"}
                        {...cellProps}
                        width={cell.column.width ? cell.column.width : "auto"}
                        style={{
                          width: cell.column.width ? cell.column.width : "auto",
                          ...(cellIndex === 0 ? { paddingLeft: "32px" } : {})
                        }}
                        cell={cell}
                        stayVisible
                      >
                        {cell.isGrouped ? (
                          // If it's a grouped cell, add an expander and row count
                          <>
                            <span {...row.getToggleRowExpandedProps()}>
                              {row.isExpanded ? (
                                <Icon sx={{ transform: "rotate(90Deg)" }}>
                                  arrow_forward_ios_icon
                                </Icon>
                              ) : (
                                <Icon>arrow_forward_ios_icon</Icon>
                              )}
                            </span>{" "}
                            {cell.render("Cell")} ({row.subRows.length})
                          </>
                        ) : cell.isAggregated ? (
                          // If the cell is aggregated, use the Aggregated
                          // renderer for cell
                          cell.render("Aggregated")
                        ) : cell.isPlaceholder ? null : ( // For cells with repeated values, render null
                          // Otherwise, just render the regular cell
                          cell.render("Cell")
                        )}
                      </DataTableBodyCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      );
    }

    return <></>;
  };

  useEffect(() => {
    setData(
      table?.rows?.filter((row) => {
        if (route[1] === "users") {
          if (filter?.status && Array.isArray(filter.status)) {
            const filteredStatus = filter.status?.filter((status) => status !== "");
            return (
              row.role?.toLowerCase().includes(filter?.role?.toLocaleLowerCase()) &&
              (filteredStatus.length === 0 || filteredStatus.includes(row.status))
            );
          } else if (filter.status) {
            return (
              row.role?.toLowerCase().includes(filter?.role?.toLocaleLowerCase()) &&
              row.status === filter?.status
            );
          } else {
            return row.role?.toLowerCase().includes(filter?.role?.toLocaleLowerCase());
          }
        } else if (route[0] === "customer-subscriptions") {
          if (filter?.company || filter?.customer) {
            return (
              row.company?.toLowerCase().includes(filter?.company?.toLocaleLowerCase()) &&
              row.customer?.toLowerCase().includes(filter?.customer?.toLocaleLowerCase())
            );
          } else {
            return row;
          }
        } else if (withStatusFilter) {
          if (filter?.status && Array.isArray(filter?.status)) {
            const filteredStatus = filter.status.filter((status) => status !== "");
            return filteredStatus.length === 0 || filteredStatus.includes(row.status);
          } else if (filter?.status) {
            return row.status === filter.status;
          } else {
            return row;
          }
        } else {
          return row;
        }
      })
    );
  }, [
    table?.rows,
    filter?.role,
    filter?.status,
    filter?.company,
    filter?.publishStatus,
    filter?.library
  ]);

  useEffect(() => {
    onFiltersChange(filter?.start, filter?.end);
  }, [filter?.start, filter?.end]);

  // A function that sets the sorted value for the table
  const setSortedValue = (column) => {
    let sortedValue;

    if (isSorted && column?.isSorted) {
      sortedValue = column?.isSortedDesc ? "desc" : "asce";
    } else if (isSorted) {
      sortedValue = "none";
    } else {
      sortedValue = false;
    }

    return sortedValue;
  };

  // Setting the entries starting point
  const entriesStart = rows.length === 0 ? 0 : pageIndex * pageSize + 1;

  // Setting the entries ending point
  const entriesEnd = Math.min((pageIndex + 1) * pageSize, rows.length);

  return (
    <TableContainer
      sx={{
        boxShadow: "none",
        overflow: "visible",
        backgroundColor: splitted ? "transparent !important" : "initial",
        display: "flex",
        flexDirection: "column",
        height: tableHeight,
        maxHeight: tableHeight,
        minHeight: typeof tableHeight === "string" ? undefined : tableHeight
      }}
      data-testid="data-table"
      className={`${splitted ? "splitted " : ""}${tableView === "tiles" ? "data-table--tile-mode" : "data-table--list-mode"}`}
    >
      {/* Header/Filters */}
      {!noTopPart && (
        <SoftBox
          backgroundColor={splitted ? "#FFFFFF" : "transparent"}
          borderRadius={splitted ? "inherit" : "initial"}
          marginBottom={splitted ? "9px" : 0}
          display={noFilter ? "none" : "flex"}
          flexDirection={{
            xs: "column",
            md: route[1] === "workflows" ? "column" : "row",
            xl: "row"
          }}
          alignItems={{
            xs: "center",
            md: "end"
          }}
          p={2}
          gap={2}
          className={`data-table--header${splitted ? " splitted-header" : ""}`}
          sx={{
            flex: "0 0 auto",
            zIndex: 2
          }}
        >
          <SoftBox
            display="flex"
            alignItems="end"
            gap={2}
            width={{ xs: "100%" }}
            flexDirection={{ xs: "column", lg: "row" }}
          >
            {pathname === "/administration/credential-management" && isShareUnshareModal && (
              <CredentialManagementSelectAll onSearchChange={onSearchChange} />
            )}
            {withStatusFilter && (
              <SoftBox width={{ xs: "100%" }} maxWidth={{ xl: "200px" }}>
                <SoftTypography variant="h6" mb={1}>
                  Status
                </SoftTypography>
                <SoftSelect
                  placeholder="Filter Status"
                  options={options?.status?.sort((a, b) => a.label.localeCompare(b.label))}
                  isMulti
                  sx={{ zIndex: 0 }}
                  hideSelectedOptions={false}
                  onChange={(selectedOptions) => {
                    const values = selectedOptions.map((option) => option.value);
                    onFilterChange("status", values);
                  }}
                />
              </SoftBox>
            )}
            {withRoleFilter && (
              <SoftBox width={{ xs: "100%" }} maxWidth={{ xl: "200px" }}>
                <SoftTypography variant="h6" mb={1}>
                  Role
                </SoftTypography>
                <SoftSelect
                  placeholder="Filter Role"
                  options={options?.roles?.length !== 0 ? options.roles : []}
                  onChange={({ value }) => onFilterChange("role", value)}
                />
              </SoftBox>
            )}
            {withSearchbar && (
              <SoftBox width={{ xs: "100%" }} maxWidth={{ xl: "250px" }}>
                <SoftInput
                  size="medium"
                  placeholder="Search..."
                  value={searchName}
                  onChange={({ currentTarget }) => {
                    setSearchName(currentTarget.value);
                    //onSearchNameChange(currentTarget.value);
                  }}
                />
              </SoftBox>
            )}
            {withTileview && (
              <SoftBox width={{ xs: "100%" }}>
                <View
                  tableView={tableView}
                  setTableView={setTableView}
                  withCalendarView={withCalendarView}
                />
              </SoftBox>
            )}
            {withConnectorsOptions && (
              <SoftBox
                display={{
                  xs: "grid",
                  lg: "flex"
                }}
                justifyContent={{ lg: "flex-end" }}
                width="100%"
                gap={1}
                sx={{
                  gridTemplateColumns: {
                    md: "repeat(2, 1fr)"
                  }
                }}
              >
                {reloadTableData && (
                  <SoftButton
                    variant="gradient"
                    size="medium"
                    color="primary"
                    onClick={reloadTableData}
                  >
                    <ThemedReloadIcon color="white" height="20px" />
                  </SoftButton>
                )}
                <ProtectedButton permissionName={PERMISSIONS.CREATE_CONNECTORS}>
                  <SoftButton
                    variant="gradient"
                    color="primary"
                    size="medium"
                    onClick={() => {
                      dispatch(openRegisterComponentDialog());
                    }}
                    sx={{
                      minWidth: "180px",
                      maxWidth: { xl: "250px" },
                      width: { xs: "100%", xl: "100%" }
                    }}
                  >
                    <Icon>add</Icon>&nbsp;create new
                  </SoftButton>
                </ProtectedButton>
              </SoftBox>
            )}
            {withFlowsOptions && (
              <SoftBox
                display={{
                  xs: "grid",
                  lg: "flex"
                }}
                justifyContent={{ lg: "flex-end" }}
                width="100%"
                gap={1}
                sx={{
                  gridTemplateColumns: {
                    md: "repeat(2, 1fr)"
                  }
                }}
              >
                <SoftButton
                  variant="gradient"
                  size="medium"
                  color="primary"
                  onClick={reloadTableData}
                >
                  <ThemedReloadIcon color="white" height="20px" />
                </SoftButton>
                {buttonPermissions?.find(({ name }) => name === PERMISSIONS.CREATE_WORKFLOWS)
                  ?.value && (
                  <SoftButton
                    variant="gradient"
                    color="primary"
                    size="medium"
                    onClick={() => dispatch(openNewFlowDialog())}
                    sx={{
                      minWidth: "180px",
                      maxWidth: { xl: "250px" },
                      width: { xs: "100%", xl: "100%" }
                    }}
                  >
                    <Icon>add</Icon>&nbsp;create new flow
                  </SoftButton>
                )}
                {getEnv("IS_COMPONENT_DOWN_WORKFLOW_IMPORT") === "active" && (
                  <SoftButton
                    variant="gradient"
                    color="primary"
                    size="medium"
                    sx={{
                      minWidth: "100px",
                      maxWidth: { xl: "250px" },
                      width: { xs: "100%", xl: "initial" }
                    }}
                    onClick={handleOpenImportFlowDialog}
                  >
                    import
                  </SoftButton>
                )}
                {getEnv("IS_COMPONENT_DOWN_WORKFLOW_EXPORT") === "active" && (
                  <SoftButton
                    variant="outlined"
                    color="primary"
                    size="medium"
                    sx={{
                      minWidth: "100px",
                      maxWidth: { xl: "250px" },
                      width: { xs: "100%", xl: "initial" }
                    }}
                  >
                    export
                  </SoftButton>
                )}
              </SoftBox>
            )}
            {withIntegrationsOptions && (
              <SoftBox
                display={{
                  xs: "grid",
                  lg: "flex"
                }}
                justifyContent={{ lg: "flex-end" }}
                width="100%"
                gap={1}
                sx={{
                  gridTemplateColumns: {
                    md: "repeat(2, 1fr)"
                  }
                }}
              >
                {reloadTableData && (
                  <SoftButton
                    variant="gradient"
                    size="medium"
                    color="primary"
                    onClick={reloadTableData}
                  >
                    <ThemedReloadIcon color="white" height="20px" />
                  </SoftButton>
                )}
                <ProtectedButton permissionName={PERMISSIONS.CREATE_CONNECTORS}>
                  <SoftButton
                    variant="gradient"
                    color="primary"
                    size="medium"
                    onClick={() => {
                      dispatch(openNewIntegrationDialog());
                    }}
                    sx={{
                      minWidth: "180px",
                      maxWidth: { xl: "250px" },
                      width: { xs: "100%", xl: "100%" }
                    }}
                  >
                    <Icon>add</Icon>&nbsp;create new
                  </SoftButton>
                </ProtectedButton>
              </SoftBox>
            )}
            {withCredentialOptions && <CredentialManagementFilter />}
            {withScheduleOptions && (
              <SoftBox
                display={{
                  xs: "grid",
                  lg: "flex"
                }}
                justifyContent={{ lg: "flex-end" }}
                width="100%"
                gap={1}
                sx={{
                  gridTemplateColumns: {
                    md: "repeat(2, 1fr)"
                  }
                }}
              >
                {reloadTableData && (
                  <SoftButton
                    variant="gradient"
                    size="medium"
                    color="primary"
                    onClick={reloadTableData}
                  >
                    <ThemedReloadIcon color="white" height="20px" />
                  </SoftButton>
                )}
                <ProtectedButton permissionName={PERMISSIONS.ADD_SCHEDULES}>
                  <SoftButton
                    variant="gradient"
                    color="primary"
                    size="medium"
                    onClick={() => setOpenSchedulerDialog(true)}
                    sx={{
                      minWidth: "180px",
                      maxWidth: { xl: "250px" },
                      width: { xs: "100%", xl: "100%" }
                    }}
                  >
                    <Icon>add</Icon>&nbsp;create new
                  </SoftButton>
                </ProtectedButton>
              </SoftBox>
            )}
            {selections && (
              <SoftBox width={{ xs: "100%" }} maxWidth={{ xl: "250px" }}>
                <SoftTypography variant="h6" mb={1}>
                  Selections
                </SoftTypography>
                <SoftSelect
                  placeholder="Choose selection"
                  options={selections.options?.sort((a, b) => a.label.localeCompare(b.label))}
                  defaultValue={selections.default ?? {}}
                  onChange={({ value }) => setSelection(value)}
                  isDisabled={isLoadingLogs}
                />
              </SoftBox>
            )}
            {canFilterPublishStatus && (
              <SoftBox width={{ xs: "100%" }} maxWidth={{ xl: "370px" }}>
                <SoftTypography variant="h6" mb={1}>
                  Publish Status
                </SoftTypography>
                <SoftSelect
                  placeholder="Filter Publish Status"
                  options={options?.publishStatus}
                  isMulti
                  sx={{ zIndex: 0 }}
                  hideSelectedOptions={false}
                  onChange={(selectedOptions) => {
                    const values = selectedOptions.map((option) => option.value);
                    onFilterChange("publishStatus", values);
                  }}
                />
              </SoftBox>
            )}
            {canFilterCustomer && (
              <SoftBox width={{ xs: "100%" }} maxWidth={{ xl: "250px" }}>
                <SoftTypography variant="h6" mb={1}>
                  Company
                </SoftTypography>
                <SoftSelect
                  placeholder="Filter Company"
                  options={options?.companies.length !== 0 ? options.companies : []}
                  onChange={({ value }) => onFilterChange("company", value)}
                />
              </SoftBox>
            )}
            {canFilterCompany && (
              <SoftBox width={{ xs: "100%" }} maxWidth={{ xl: "250px" }}>
                <SoftTypography variant="h6" mb={1}>
                  Customer
                </SoftTypography>
                <SoftSelect
                  placeholder="Filter Customer"
                  options={options?.customers.length !== 0 ? options.customers : []}
                  onChange={({ value }) => onFilterChange("customer", value)}
                />
              </SoftBox>
            )}
            {canFilterDates && (
              <>
                <SoftBox width={{ xs: "100%" }} maxWidth={{ xl: "200px" }}>
                  <SoftTypography variant="h6" mb={1}>
                    Start
                  </SoftTypography>
                  <SoftDatePicker
                    className="date-picker"
                    input={{ placeholder: "Select a date" }}
                    value={filter.start}
                    onChange={(value, valueStr) => {
                      onFilterChange("start", valueStr);
                    }}
                  />
                </SoftBox>
                <SoftBox width={{ xs: "100%" }} maxWidth={{ xl: "200px" }}>
                  <SoftTypography variant="h6" mb={1}>
                    End
                  </SoftTypography>
                  <SoftDatePicker
                    className="date-picker"
                    input={{ placeholder: "Select a date" }}
                    value={filter.end}
                    onChange={(value, valueStr) => {
                      onFilterChange("end", valueStr);
                    }}
                  />
                </SoftBox>
              </>
            )}
            {withLogsFilter && (
              <LogsFilter
                setFilterDate={setFilterDate}
                onSearchChange={onSearchChange}
                canFilter={canFilter}
              />
            )}
            {withUsersOptions && (
              <SoftBox
                display={{
                  xs: "grid",
                  lg: "flex"
                }}
                justifyContent={{
                  lg: "flex-end"
                }}
                width="100%"
                gap={1}
                sx={{
                  gridTemplateColumns: {
                    md: "repeat(2, 1fr)"
                  }
                }}
              >
                <SoftButton
                  variant="gradient"
                  size="medium"
                  color="primary"
                  onClick={reloadUsersList}
                >
                  <ThemedReloadIcon color="white" height="20px" />
                </SoftButton>
                <ProtectedButton permissionName={PERMISSIONS.INVITE_USERS}>
                  <SoftButton
                    variant="gradient"
                    color="primary"
                    size="medium"
                    sx={{
                      minWidth: "195px",
                      width: { xs: "100%" },
                      maxWidth: { xl: "250px" }
                    }}
                    onClick={handleOpenInviteModal}
                  >
                    <Icon>add</Icon>&nbsp;invite users
                  </SoftButton>
                </ProtectedButton>
              </SoftBox>
            )}
            {pathname === "/marketplace" && hiddenTopButtons === false && (
              <SoftBox width={{ xs: "100%" }}>
                <ViewButtons setView={setView} view={view} />
              </SoftBox>
            )}
            {route[1] === "build-integrations" && hiddenTopButtons === false && (
              <SoftBox width={{ xs: "100%" }}>
                <ViewButtons
                  setFlowView={setFlowView}
                  setView={setView}
                  flowView={flowView}
                  view={view}
                />
              </SoftBox>
            )}
            {route[2] === "explore-more" && hiddenTopButtons === false && (
              <SoftBox width={{ xs: "100%" }}>
                <ViewButtons setView={setView} view={view} />
              </SoftBox>
            )}
            {route[1] === "build-integrations" && hiddenTopButtons === false && (
              <SoftBox
                display={{
                  xs: "grid",
                  lg: "flex"
                }}
                justifyContent={{
                  lg: "flex-end"
                }}
                width="100%"
                gap={1}
                sx={{
                  gridTemplateColumns: {
                    md: "repeat(2, 1fr)"
                  }
                }}
              >
                <SoftButton
                  variant="gradient"
                  size="medium"
                  color="primary"
                  onClick={reloadTableData}
                >
                  <ThemedReloadIcon color="white" height="20px" />
                </SoftButton>
                {buttonPermissions?.find(({ name }) => name === PERMISSIONS.CREATE_WORKFLOWS)
                  ?.value && (
                  <SoftButton
                    variant="gradient"
                    color="primary"
                    size="medium"
                    onClick={() => {
                      dispatch(openNewFlowDialog());
                    }}
                    sx={{
                      minWidth: "250px",
                      maxWidth: { xl: "250px" },
                      width: { xs: "100%", xl: "100%" }
                    }}
                  >
                    <Icon>add</Icon>&nbsp;create new integration
                  </SoftButton>
                )}
                {/* </ProtectedButton> */}
                <SoftButton
                  variant="outlined"
                  color="primary"
                  size="medium"
                  sx={{
                    minWidth: "100px",
                    maxWidth: { xl: "250px" },
                    width: { xs: "100%", xl: "initial" }
                  }}
                  onClick={handleOpenImportFlowDialog}
                >
                  import
                </SoftButton>
                {getEnv("IS_COMPONENT_DOWN_EXPORT_BTN") === "active" && (
                  <SoftButton
                    variant="outlined"
                    color="primary"
                    size="medium"
                    sx={{
                      minWidth: "100px",
                      maxWidth: { xl: "250px" },
                      width: { xs: "100%", xl: "initial" }
                    }}
                  >
                    export
                  </SoftButton>
                )}
              </SoftBox>
            )}
            {route[1] === "roles-and-permissions" && (
              <SoftBox
                display={{
                  xs: "grid",
                  lg: "flex"
                }}
                justifyContent={{
                  lg: "flex-end"
                }}
                width="100%"
                gap={1}
                sx={{
                  gridTemplateColumns: {
                    md: "repeat(2, 1fr)"
                  }
                }}
              >
                <SoftButton
                  variant="gradient"
                  size="medium"
                  color="primary"
                  onClick={reloadRoleList}
                  sx={{
                    height: "44px",
                    display: "flex",
                    alignItems: "center"
                  }}
                >
                  <ThemedReloadIcon color="white" height="20px" />
                </SoftButton>
                <ProtectedButton permissionName={PERMISSIONS.CREATE_ROLES}>
                  <SoftButton
                    variant="gradient"
                    size="medium"
                    color="info"
                    onClick={handleOpenAddRoleModal}
                    data-testid="add-role-btn"
                    sx={{
                      minWidth: "250px",
                      maxWidth: { xl: "250px" },
                      width: { xs: "100%", xl: "100%" }
                    }}
                  >
                    <AddIcon sx={{ fontSize: "1.25rem", marginRight: "8px" }} />
                    Add Role
                  </SoftButton>
                </ProtectedButton>
              </SoftBox>
            )}
          </SoftBox>
        </SoftBox>
      )}
      {/* Table body with fixed height and scroll */}
      <SoftBox
        sx={{
          flex: "1 1 auto",
          overflowY: "auto",
          maxHeight: tableHeight,
          backgroundColor: splitted
            ? tableView === "tiles" || flowView === 2
              ? "transparent !important"
              : isDarkTheme
                ? THEME_COLORS.DARK.PRIMARY_BG
                : THEME_COLORS.LIGHT.SECONDARY_BG
            : "transparent",
          opacity: splitted ? 1 : 1,
          borderTopLeftRadius: splitted ? "inherit" : "initial",
          borderTopRightRadius: splitted ? "inherit" : "initial",
          borderBottomLeftRadius: "0",
          borderBottomRightRadius: "0"
        }}
        className={`data-table--body${splitted ? " splitted-data" : ""}${tableView === "tiles" ? " data-table--tiles" : ""}`}
      >
        <SoftBox sx={{ overflowX: stayVisible ? "visible" : "auto" }}>{renderView()}</SoftBox>
      </SoftBox>

      {/* Footer/Pagination */}
      <SoftBox
        className="data-table--footer"
        display="flex"
        alignItems="center"
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent={{ sm: "space-between" }}
        gap={3}
        p={!showTotalEntries && pageOptions.length === 1 ? 0 : 3}
        sx={{
          flex: "0 0 auto",
          zIndex: 2,
          background: isDarkTheme
            ? THEME_COLORS.DARK.SECONDARY_BG
            : THEME_COLORS.LIGHT.SECONDARY_BG,
          borderBottomLeftRadius: splitted ? "inherit" : "initial",
          borderBottomRightRadius: splitted ? "inherit" : "initial",
          borderTopLeftRadius: tableView === "tiles" && splitted ? "inherit" : "initial",
          borderTopRightRadius: tableView === "tiles" && splitted ? "inherit" : "initial"
        }}
      >
        <SoftBox
          display="flex"
          alignItems="center"
          flexDirection={{ xs: "column", md: "row" }}
          gap={3}
        >
          {showTotalEntries && (
            <SoftTypography variant="button" color="text" fontWeight="regular">
              Showing {entriesStart} to {entriesEnd} of {rows.length} entries
            </SoftTypography>
          )}
          {entriesPerPage && (
            <SoftBox
              className="data-table--entries"
              display="flex"
              alignItems="center"
              flexDirection={{ xs: "column", lg: "row" }}
            >
              <SoftBox sx={{ width: "80px" }}>
                <SoftSelect
                  menuPlacement="bottom"
                  defaultValue={{
                    value: defaultValue,
                    label: defaultValue
                  }}
                  options={entries.map((entry) => ({
                    value: entry,
                    label: entry
                  }))}
                  onChange={setEntriesPerPage}
                />
              </SoftBox>
              <SoftTypography variant="caption" color="text">
                &nbsp;&nbsp;entries per page
              </SoftTypography>
            </SoftBox>
          )}
        </SoftBox>
        {pageOptions.length > 1 && (
          <SoftPagination
            variant={pagination.variant ? pagination.variant : "gradient"}
            color={pagination.color ? pagination.color : "info"}
          >
            {canPreviousPage && (
              <SoftPagination item onClick={() => previousPage()}>
                <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
              </SoftPagination>
            )}
            {renderPagination.length > 6 ? (
              <SoftBox width="5rem" mx={1}>
                <SoftInput
                  inputProps={{
                    type: "number",
                    min: 1,
                    max: customizedPageOptions.length
                  }}
                  value={customizedPageOptions[pageIndex]}
                  onChange={(handleInputPagination, handleInputPaginationValue)}
                />
              </SoftBox>
            ) : (
              renderPagination
            )}
            {canNextPage && (
              <SoftPagination item onClick={() => nextPage()}>
                <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
              </SoftPagination>
            )}
          </SoftPagination>
        )}
      </SoftBox>
    </TableContainer>
  );
}

// Typechecking props for the DataTable
DataTable.propTypes = {
  entriesPerPage: PropTypes.oneOfType([
    PropTypes.shape({
      defaultValue: PropTypes.number,
      entries: PropTypes.arrayOf(PropTypes.number)
    }),
    PropTypes.bool
  ]),
  canFilterRole: PropTypes.bool,
  canFilterDates: PropTypes.bool,
  canFilterCompany: PropTypes.bool,
  canFilterCustomer: PropTypes.bool,
  showTotalEntries: PropTypes.bool,
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(["contained", "gradient"]),
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "light"
    ])
  }),
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,
  hover: PropTypes.bool,
  options: PropTypes.shape({
    roles: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired
      })
    ),
    status: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired
      })
    ),
    companies: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired
      })
    ),
    customers: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired
      })
    )
  }),
  setOpenInviteModal: PropTypes.func,
  onFiltersChange: PropTypes.func,
  onRowClick: PropTypes.func,
  tableView: PropTypes.string,
  setTableView: PropTypes.func,
  splitted: PropTypes.bool
};

export default DataTable;
